import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getExtractPdfStatus,
  getActiveContracts,
  getInactiveContracts,
  getContractById,
  getContractPdf,
  getContractAnalytics,
  getContractReturnAnalytics,
  getCumulativeOverview,
  getCumulativeSupply,
  getCumulativeReturn,
  getCumulativeSupplySources,
  getCumulativeSupplyDestinations,
  getTraders,
} from "../../Services/banking/BankingCalc";

// Helper for consistent error handling
const handleApiError = (err, rejectWithValue) => {
  return rejectWithValue(
    err.response?.data?.message || err.message || "Something went wrong",
  );
};

// --------- Upload & Extract ---------

export const fetchExtractPdfStatus = createAsyncThunk(
  "bankingCalc/fetchExtractPdfStatus",
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await getExtractPdfStatus(taskId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// --------- Contracts ---------

export const fetchActiveContracts = createAsyncThunk(
  "bankingCalc/fetchActiveContracts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getActiveContracts();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchInactiveContracts = createAsyncThunk(
  "bankingCalc/fetchInactiveContracts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getInactiveContracts();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchContractById = createAsyncThunk(
  "bankingCalc/fetchContractById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getContractById(id);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchContractPdf = createAsyncThunk(
  "bankingCalc/fetchContractPdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getContractPdf(id);
      return response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// --------- Analytics ---------

export const fetchContractAnalytics = createAsyncThunk(
  "bankingCalc/fetchContractAnalytics",
  async ({ id, groupBy }, { rejectWithValue }) => {
    try {
      const response = await getContractAnalytics(id, groupBy);
      console.log("🚀 ~ response?.data?.data:", response?.data?.data)
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchContractReturnAnalytics = createAsyncThunk(
  "bankingCalc/fetchContractReturnAnalytics",
  async ({ id, groupBy }, { rejectWithValue }) => {
    try {
      const response = await getContractReturnAnalytics(id, groupBy);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// --------- Cumulative ---------

export const fetchCumulativeOverview = createAsyncThunk(
  "bankingCalc/fetchCumulativeOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCumulativeOverview();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchCumulativeSupply = createAsyncThunk(
  "bankingCalc/fetchCumulativeSupply",
  async (groupBy, { rejectWithValue }) => {
    try {
      const response = await getCumulativeSupply(groupBy);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchCumulativeReturn = createAsyncThunk(
  "bankingCalc/fetchCumulativeReturn",
  async (groupBy, { rejectWithValue }) => {
    try {
      const response = await getCumulativeReturn(groupBy);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchCumulativeSupplySources = createAsyncThunk(
  "bankingCalc/fetchCumulativeSupplySources",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCumulativeSupplySources();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchCumulativeSupplyDestinations = createAsyncThunk(
  "bankingCalc/fetchCumulativeSupplyDestinations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCumulativeSupplyDestinations();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// --------- Reference ---------

export const fetchTraders = createAsyncThunk(
  "bankingCalc/fetchTraders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTraders();
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// ==========================================
// Slice Definition
// ==========================================

const initialState = {
  // Data
  extractPdfStatus: null,
  activeContracts: [],
  inactiveContracts: [],
  contractById: null,
  contractPdf: null,
  contractAnalytics: null,
  contractReturnAnalytics: null,
  contractAnalyticsLoading: false,
  contractAnalyticsError: null,
  contractReturnAnalyticsLoading: false,
  contractReturnAnalyticsError: null,
  cumulativeOverview: null,
  cumulativeSupply: null,
  cumulativeReturn: null,
  cumulativeSupplySources: [],
  cumulativeSupplyDestinations: [],
  traders: [],

  // Loading & Error
  loading: false,
  error: null,
};

const BankingCalcSlice = createSlice({
  name: "bankingCalc",
  initialState,
  reducers: {
    clearContractById: (state) => {
      state.contractById = null;
    },
    clearContractAnalytics: (state) => {
      state.contractAnalytics = null;
      state.contractReturnAnalytics = null;
    },
  },
  extraReducers: (builder) => {
    // --- fetchExtractPdfStatus ---
    builder
      .addCase(fetchExtractPdfStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExtractPdfStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.extractPdfStatus = action.payload;
      })
      .addCase(fetchExtractPdfStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchActiveContracts ---
    builder
      .addCase(fetchActiveContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.activeContracts = action.payload || [];
      })
      .addCase(fetchActiveContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchInactiveContracts ---
    builder
      .addCase(fetchInactiveContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.inactiveContracts = action.payload || [];
      })
      .addCase(fetchInactiveContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchContractById ---
    builder
      .addCase(fetchContractById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractById.fulfilled, (state, action) => {
        state.loading = false;
        state.contractById = action.payload;
      })
      .addCase(fetchContractById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchContractPdf ---
    builder
      .addCase(fetchContractPdf.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractPdf.fulfilled, (state, action) => {
        state.loading = false;
        state.contractPdf = action.payload;
      })
      .addCase(fetchContractPdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchContractAnalytics ---
    builder
      .addCase(fetchContractAnalytics.pending, (state) => {
        state.contractAnalyticsLoading = true;
        state.contractAnalyticsError = null;
      })
      .addCase(fetchContractAnalytics.fulfilled, (state, action) => {
        state.contractAnalyticsLoading = false;
        state.contractAnalytics = action.payload;
      })
      .addCase(fetchContractAnalytics.rejected, (state, action) => {
        state.contractAnalyticsLoading = false;
        state.contractAnalyticsError = action.payload;
      });

    // --- fetchContractReturnAnalytics ---
    builder
      .addCase(fetchContractReturnAnalytics.pending, (state) => {
        state.contractReturnAnalyticsLoading = true;
        state.contractReturnAnalyticsError = null;
      })
      .addCase(fetchContractReturnAnalytics.fulfilled, (state, action) => {
        state.contractReturnAnalyticsLoading = false;
        state.contractReturnAnalytics = action.payload;
      })
      .addCase(fetchContractReturnAnalytics.rejected, (state, action) => {
        state.contractReturnAnalyticsLoading = false;
        state.contractReturnAnalyticsError = action.payload;
      });

    // --- fetchCumulativeOverview ---
    builder
      .addCase(fetchCumulativeOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCumulativeOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.cumulativeOverview = action.payload;
      })
      .addCase(fetchCumulativeOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchCumulativeSupply ---
    builder
      .addCase(fetchCumulativeSupply.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCumulativeSupply.fulfilled, (state, action) => {
        state.loading = false;
        state.cumulativeSupply = action.payload;
      })
      .addCase(fetchCumulativeSupply.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchCumulativeReturn ---
    builder
      .addCase(fetchCumulativeReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCumulativeReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.cumulativeReturn = action.payload;
      })
      .addCase(fetchCumulativeReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchCumulativeSupplySources ---
    builder
      .addCase(fetchCumulativeSupplySources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCumulativeSupplySources.fulfilled, (state, action) => {
        state.loading = false;
        state.cumulativeSupplySources = action.payload || [];
      })
      .addCase(fetchCumulativeSupplySources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchCumulativeSupplyDestinations ---
    builder
      .addCase(fetchCumulativeSupplyDestinations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCumulativeSupplyDestinations.fulfilled, (state, action) => {
        state.loading = false;
        state.cumulativeSupplyDestinations = action.payload || [];
      })
      .addCase(fetchCumulativeSupplyDestinations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchTraders ---
    builder
      .addCase(fetchTraders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTraders.fulfilled, (state, action) => {
        state.loading = false;
        state.traders = action.payload || [];
      })
      .addCase(fetchTraders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearContractById, clearContractAnalytics } =
  BankingCalcSlice.actions;

export default BankingCalcSlice.reducer;
